import { useState, useEffect } from "react";
import { searchFakturowniaClients } from "../api/clients";
import type { Client } from "../types/client";
import { colors, spacing, borderRadius, typography, styles } from "../styles/theme";

type Props = {
  onSelect: (client: Client) => void;
  selected?: Client | null;
};

const ClientSearch = ({ onSelect, selected }: Props) => {
  const [fields, setFields] = useState({
    name: "",
    email: "",
    id: "",
    shortcut: "",
    tax_no: ""
  });
  const [results, setResults] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(true);

  // Obsługa zmiany w dowolnym polu
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFields(f => ({ ...f, [e.target.name]: e.target.value }));
    setShowResults(true); // Pokaż wyniki przy wpisywaniu
  };

  // Szukaj tylko, jeśli któreś pole nie jest puste
  useEffect(() => {
    setError(null);
    if (Object.values(fields).every(v => !v.trim())) {
      setResults([]);
      return;
    }
    setLoading(true);
    searchFakturowniaClients(fields)
      .then(setResults)
      .catch(() => setError("Błąd wyszukiwania klientów!"))
      .finally(() => setLoading(false));
  }, [fields]);

  const handleSelect = (client: Client) => {
    onSelect(client);
    setShowResults(false); // Ukryj listę po wyborze
    setFields({ name: "", email: "", id: "", shortcut: "", tax_no: "" }); // Wyczyść pola
    setResults([]);
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: spacing.sm }}>
        <input
          name="name"
          placeholder="Nazwa"
          value={fields.name}
          onChange={handleChange}
          style={styles.input}
        />
        <input
          name="email"
          placeholder="Email"
          value={fields.email}
          onChange={handleChange}
          style={styles.input}
        />
        <input
          name="id"
          placeholder="ID"
          value={fields.id}
          onChange={handleChange}
          style={{...styles.input, maxWidth: 100}}
        />
        <input
          name="shortcut"
          placeholder="Skrót"
          value={fields.shortcut}
          onChange={handleChange}
          style={{...styles.input, maxWidth: 120}}
        />
        <input
          name="tax_no"
          placeholder="NIP"
          value={fields.tax_no}
          onChange={handleChange}
          style={{...styles.input, maxWidth: 140}}
        />
      </div>

      {loading && (
        <div style={{
          padding: spacing.md,
          color: colors.textSecondary,
          textAlign: 'center',
          marginTop: spacing.sm,
        }}>
          🔍 Wyszukiwanie...
        </div>
      )}

      {error && (
        <div style={{
          color: colors.errorDark,
          background: colors.errorLight,
          padding: spacing.md,
          borderRadius: borderRadius.sm,
          marginTop: spacing.sm,
          border: `1px solid ${colors.error}`,
        }}>
          ❌ {error}
        </div>
      )}

      {showResults && results.length > 0 && (
        <ul style={{
          maxHeight: 200,
          overflowY: "auto",
          background: colors.background,
          margin: `${spacing.sm}px 0 0 0`,
          padding: spacing.sm,
          listStyle: 'none',
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.sm,
        }}>
          {results.map(client => (
            <li key={client.id}
              style={{
                cursor: "pointer",
                background: colors.gray50,
                padding: spacing.md,
                marginBottom: spacing.xs,
                borderRadius: borderRadius.sm,
                border: `1px solid ${colors.borderLight}`,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.primaryLight;
                e.currentTarget.style.borderColor = colors.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.gray50;
                e.currentTarget.style.borderColor = colors.borderLight;
              }}
              onClick={() => handleSelect(client)}
            >
              <div style={{ fontWeight: typography.fontWeight.semibold, marginBottom: spacing.xs }}>
                {client.name}
              </div>
              <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                ID: {client.id}
                {client.shortcut && <> • Skrót: {client.shortcut}</>}
                {client.email && <> • {client.email}</>}
                {client.tax_no && <> • NIP: {client.tax_no}</>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && !error && Object.values(fields).some(v => v.trim()) && results.length === 0 && (
        <div style={{
          color: colors.textSecondary,
          fontSize: typography.fontSize.sm,
          marginTop: spacing.sm,
          padding: spacing.md,
          textAlign: 'center',
          background: colors.gray50,
          borderRadius: borderRadius.sm,
        }}>
          🔍 Brak wyników dla podanych kryteriów
        </div>
      )}
    </div>
  );
};

export default ClientSearch;
